import { memo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { PromptEditor } from './PromptEditor';
import { SectionContainer } from './SectionContainer';
import { Button } from '~/components/ui/Button';
import { useStateTracked } from '~/lib/hooks/useStateTracked';
import { settingsService } from '~/lib/services/settings-service';
import { logger } from '~/utils/logger';

const BrowseLink = () => (
  <Button size="md" asChild variant="link-color" className="!no-underline items-start justify-start gap-2 mb-4">
    <a href="https://support.bolt.new/best-practices/prompting-effectively" target="_blank">
      <span className="i-heroicons:arrow-top-right-on-square text-lg" />
      <span>Browse examples of instructions</span>
    </a>
  </Button>
);

export const KnowledgePersonal = memo(() => {
  const [systemPrompt, setSystemPrompt, systemPromptChanged] = useStateTracked(() =>
    settingsService.readValue('knowledge', 'systemPrompt'),
  );

  const handleSystemPromptChange = useCallback(async (value: string) => {
    try {
      const trimmedValue = value.trim();

      await settingsService.set('knowledge', 'systemPrompt', trimmedValue);

      setSystemPrompt(trimmedValue, true);

      toast.success('System prompt updated');
    } catch (error) {
      logger.error(error);
      toast.error('Failed to update system prompt');
    }
  }, []);

  return (
    <SectionContainer title="Knowledge" className="flex flex-col h-full min-h-0 pb-6">
      <BrowseLink />
      <PromptEditor
        value={systemPrompt}
        saveDisabled={!systemPromptChanged}
        placeholder="Define default instructions that Bolt will follow in every conversation. This helps shape how Bolt understands and responds to your questions."
        description="Global instructions"
        setValue={setSystemPrompt}
        onSave={handleSystemPromptChange}
        innerClassName="h-full min-h-0 mt-2"
      />
    </SectionContainer>
  );
});

KnowledgePersonal.displayName = 'KnowledgePersonal';
